/**
 * LIBRERIA PORTALCASH SHARED
 * Centralized Exports for Components, Services, Types, and Utilities
 */

// ===== 1. FRAMEWORK & CORE TYPES =====
export type { Component } from "vue";

// ===== 2. UI COMPONENTS =====
export { default as AutocompleteInput } from "./components/autoComplete/AutocompleteInput.vue";
export { default as AutocompleteRutInput } from "./components/autoComplete/AutocompleteRutInput.vue";
export { default as CurrencySelector } from "./components/ui/CurrencySelector.vue";
export { default as CustomButton } from "./components/ui/CustomButton.vue";
export { default as CustomCheckBox } from "./components/ui/CustomCheckBox.vue";
export { default as CustomDatePicker } from "./components/ui/CustomDatePicker.vue";
export { default as CustomDialog } from "./components/ui/CustomDialog.vue";
export { default as CustomInput } from "./components/ui/CustomInput.vue";
export { default as CustomInputNumber } from "./components/ui/CustomInputNumber.vue";
export { default as CustomLineChart } from "./components/ui/CustomLineChart.vue";
export { default as CustomModalConfirm } from "./components/ui/CustomModalConfirm.vue";
export { default as CustomModalInfoTable } from "./components/ui/CustomModalInfoTable.vue";
export { default as CustomMultiButton } from "./components/ui/CustomMultiButton.vue";
export { default as CustomMultiSelect } from "./components/ui/CustomMultiSelect.vue";
export { default as CustomPersonalData } from "./components/ui/CustomPersonalData.vue";
export { default as CustomPie } from "./components/ui/CustomPie.vue";
export { default as CustomRadioButton } from "./components/ui/CustomRadioButton.vue";
export { default as CustomSelect } from "./components/ui/CustomSelect.vue";
export { default as CustomSelectCountry } from "./components/ui/CustomSelectCountry.vue";
export { default as CustomStepper } from "./components/ui/CustomStepper.vue";
export { default as CustomSwitch } from "./components/ui/CustomSwitch.vue";
export { default as CustomTabs } from "./components/ui/CustomTabs.vue";
export { default as CustomTextArea } from "./components/ui/CustomTextArea.vue";
export { default as CustomToast } from "./components/ui/CustomToast.vue";
export { default as CustomUploadExcel } from "./components/ui/CustomUploadExcel.vue";
export { default as MainCard } from "./components/ui/MainCard.vue";
export { default as MainTable } from "./components/ui/MainTable.vue";
export { default as ModalAutorization } from "./components/ui/ModalAutorization.vue";
export { default as UploadFileDialog } from "./components/ui/UploadFileDialog.vue";
export { default as ModalAutorizationOperation } from "./components/ui/ModalAutorizationOperation.vue";
export { default as CustomInfoOperationTable } from "./components/ui/CustomInfoOperationTable.vue";

// ===== 3. OPERATION COMPONENTS =====
export { default as BalanceCard } from "./components/operations/BalanceCard.vue";
export { default as DetailsCard } from "./components/operations/DetailsCard.vue";
export { default as ObservationCard } from "./components/operations/ObservationCard.vue";
export { default as ThirdPartyCard } from "./components/operations/ThirdPartyCard.vue";

// ===== 4. STORES =====
export * from "./stores";

// ===== 5. SERVICES & LOGIC =====

// --- Auth & User ---
export * from "./service/userAuth/types";

// --- Account & Heritage ---
export { accountTypeService } from "./service/account/accountTypeService";
export { heritageChartService } from "./service/heritage/heritageChartService";
export { heritageService } from "./service/heritage/heritageService";
export * from "./service/account/types";
export * from "./service/heritage/types";

// --- Representative & Beneficiary ---
export { beneficiaryService } from "./service/beneficiary/beneficiaryService";
export { representativeService } from "./service/representative/representativeService";
export * from "./service/beneficiary/types";
export * from "./service/representative/types";

// --- General Services ---
export { accountOriginService } from "./service/general/accountOriginService";
export { bankListClpService } from "./service/general/banksClpService";
export { creadiCorpService } from "./service/general/banksCrediCorpService";
export { clientAmountService } from "./service/general/clientAmountService";
export { clientTypeService } from "./service/clientType/clientTypeService";
export { countryService } from "./service/general/countryService";
export { currencyService } from "./service/currencyType/currencyService";
export { movementTypeService } from "./service/general/movementTypeService";
export { operationService } from "./service/general/operationService";
export { paymentMethodsService } from "./service/general/paymentMethodsService";
export { surveyService } from "./service/general/surveyService";
export { transactionService } from "./service/general/transactionService";
export { valuteService } from "./service/general/valuteService";
export * from "./service/clientType/types";

// --- Global Service Types & WebSocket ---
export * from "./service/webSocketConnection";
export * from "./service/status/status";
export * from "./service/status/type";

// --- Admin & Analyst ---
export { analystService } from "./service/analyst/analystService";
export { complianceService } from "./service/compliance/complianceService";
export { LegalService } from "./service/legal/legalService";
export { ClientSearchService } from "./service/searchClient/clientSearchService";
export * from "./service/compliance/type";
export * from "./service/legal/type";
export type * from "./service/searchClient/type";

// --- Transfers & Movements ---
export { movementService } from "./service/myMovement/myMovementService";
export { transferService } from "./service/transfer/transferService";
export * from "./service/transfer/type";
export type {
  contributionList,
  forexList,
  lastMovement,
  movementList,
  operationStatus,
  operationType,
} from "./service/myMovement/type";

// --- Forex & FX Trading ---
export { ForexChartService } from "./service/forex/graficLine/charLineService";
export { forexOperationsService } from "./service/forex/forexOperations/forexOperationsService";
export { fxTradingService } from "./service/fxPositionAdministration/trading/tradingFxService";
export * from "./service/forex/closedOperations/closedOperationsService";
export * from "./service/forex/graficLine/type";
export * from "./service/forex/myAlerts/myAlertsService";
export * from "./service/forex/orderFx/orderFxService";
export type * from "./service/fxPositionAdministration/trading/type";
export type {
  mainCurrencyData,
  openPrice,
  operation,
  secundaryCurrencyData,
} from "./service/forex/forexOperations/type";
export type { orderFxData, orderFxList } from "./service/forex/orderFx/type";
export type {
  closedOperationsData,
  operationsList,
} from "./service/forex/closedOperations/type";
export type {
  alertList,
  myAlertsData,
  typeNotification,
} from "./service/forex/myAlerts/type";

// --- FX Distribution & Risk ---
export { fxSpreadService } from "./service/fxPositionAdministration/spread/spreadService";
export { spotCloseSegmentService } from "./service/fxDistributionAdministration/spotCloseSegment/spotCloseSegmentService";
export { spotForwadSegmentService } from "./service/fxDistributionAdministration/spotForwadSegment/spotForwadSegmentService";
export * from "./service/reportFX/reportFxService";
export * from "./service/fxDistributionAdministration/spotCloseSegment/type";
export type { reportFxData, reportFxList } from "./service/reportFX/type";
export type { spreadList } from "./service/fxPositionAdministration/spread/type";
export type {
  SpotForwardOperation,
  SpotForwardSegmentContent,
} from "./service/fxDistributionAdministration/spotForwadSegment/type";
export type {
  riskData,
  riskQuotaDaily,
  riskReq,
  riskSettlement,
  risksList,
} from "./service/risk/type";

// ===== 6. EXPLICIT TYPE RE-EXPORTS (Handling Conflicts) =====
export type {
  accountOrigin,
  bankClp,
  clientAmount,
  country as GeneralCountry,
  credicorpBank,
  movementType,
  movementTypeItem,
  operationType as GeneralOperationType,
  paymentMethod,
  survey,
  valute,
} from "./service/general/types";

export type {
  bankData,
  CardConfig,
  clientData,
  country as AnalystCountry,
  currencyType as AnalystCurrencyType,
  fertilizerCharges,
  operationDetails,
  operations,
  ResourcesOrigin,
} from "./service/analyst/types";

export * from "./types/general";

export type { currencyType } from "./service/currencyType/types";

// ===== 7. CONFIGURATIONS =====
export * from "./service/config/UrlConfig";

// ===== 8. COMPOSABLES =====
export * from "./composables/useB2CAuth";
export * from "./composables/useFeatureFlags";
export * from "./composables/useFormatPrice";
export * from "./composables/useUrlRedirect";
export * from "./composables/useWindowSize";

// ===== 9. UTILITIES & CONSTANTS =====
export * from "./constants/general";
export * from "./utils/base64Transformer";
export * from "./utils/redirectHandler";
export * from "./utils/rutFormatter";
export * from "./utils/validation";

// ===== 10. PLUGINS & STYLES =====
export { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import "./assets/main.css";

// ===== 11. VERSION =====
export const version = "1.0.3";
